/**
 * Track 7 — resource gathering: deposit grades, star-type modifiers, station tiers, logistics by
 * distance and builder ships. The ONE place that owns these numbers (docs/game-clock.md section 6,
 * docs/sim/mining/ for the evidence). Pure functions, no I/O: the server computes, the client shows.
 *
 * Units: distances in light years (ly), durations in real hours (`GAME_HOURS`), rates per hour.
 */
import { GAME_HOURS } from './universe.js';

// ---- Deposit grades (revealed by the survey) ----

export const DEPOSIT_GRADES = ['poor', 'normal', 'rich', 'exotic'] as const;
export type DepositGrade = (typeof DEPOSIT_GRADES)[number];

export function isDepositGrade(v: unknown): v is DepositGrade {
  return typeof v === 'string' && (DEPOSIT_GRADES as readonly string[]).includes(v);
}

export const DEPOSIT_GRADE_INFO: Record<DepositGrade, { label: string; multiplier: number; hint: string }> = {
  poor: { label: 'Szegény', multiplier: 0.6, hint: 'a normál kitermelés 60%-a' },
  normal: { label: 'Átlagos', multiplier: 1, hint: 'alapkitermelés' },
  rich: { label: 'Gazdag', multiplier: 1.5, hint: 'a normál 150%-a' },
  exotic: { label: 'Egzotikus', multiplier: 2.2, hint: 'a normál 220%-a, ritka' },
};

/** Roll thresholds (0..99, after the richness shift): < poor → poor, < normal → normal, < rich → rich, else exotic. */
const GRADE_THRESHOLDS = { poor: 22, normal: 72, rich: 94 } as const;

/** Grade from a deterministic roll in 0..99 and the system's resource richness (0..100): rich systems skew to better grades. */
export function depositGradeFromRoll(roll: number, richness: number): DepositGrade {
  const shifted = roll + (richness - 50) * 0.3;
  if (shifted < GRADE_THRESHOLDS.poor) return 'poor';
  if (shifted < GRADE_THRESHOLDS.normal) return 'normal';
  if (shifted < GRADE_THRESHOLDS.rich) return 'rich';
  return 'exotic';
}

// ---- Star type modifiers ----

export const STAR_TYPES = ['yellow_dwarf', 'red_dwarf', 'blue_giant', 'white_dwarf', 'neutron_star', 'black_hole', 'binary'] as const;
export type MiningStarType = (typeof STAR_TYPES)[number];

/** Output factor of a deposit by the type of its star, separately for metal and energy deposits. */
export const STAR_TYPE_MODIFIERS: Record<MiningStarType, { metal: number; energy: number; note: string }> = {
  yellow_dwarf: { metal: 1, energy: 1, note: 'kiegyensúlyozott' },
  red_dwarf: { metal: 1.05, energy: 0.75, note: 'halvány fény: kevés energia' },
  blue_giant: { metal: 0.85, energy: 1.6, note: 'hatalmas sugárzás: sok energia, kevés szilárd anyag' },
  white_dwarf: { metal: 1.1, energy: 0.9, note: 'sűrű maradvány: kicsivel több fém' },
  neutron_star: { metal: 1.5, energy: 1.1, note: 'extrém sűrű anyag: sok fém' },
  black_hole: { metal: 1, energy: 1.5, note: 'akkréciós energia' },
  binary: { metal: 1.1, energy: 1.25, note: 'két csillag: több fém és energia' },
};

export function starTypeFactor(starType: string, resource: 'metal' | 'energy'): number {
  return (STAR_TYPE_MODIFIERS as Record<string, { metal: number; energy: number }>)[starType]?.[resource] ?? 1;
}

/** Combined output factor stored with a new deposit (grade × star type), rounded to 3 decimals. */
export function depositOutputFactor(grade: DepositGrade, starType: string, resource: 'metal' | 'energy'): number {
  return Math.round(DEPOSIT_GRADE_INFO[grade].multiplier * starTypeFactor(starType, resource) * 1000) / 1000;
}

// ---- Station tiers ----

export interface StationTier {
  level: number;
  name: string;
  /** Output multiplier of the deposit's yield. */
  output: number;
  /** Metal upkeep per hour (every station type). */
  metalUpkeep: number;
  /** Energy upkeep per hour (mining stations only). */
  miningEnergyUpkeep: number;
  /** Price of the upgrade INTO this tier (null for tier 1: that is the build cost). */
  upgradeCost: { metal: number; energy: number } | null;
  /** Real hours the upgrade INTO this tier takes (null for tier 1). */
  upgradeHours: number | null;
}

export const STATION_TIERS: readonly StationTier[] = [
  { level: 1, name: 'Alapállomás', output: 1, metalUpkeep: 10, miningEnergyUpkeep: 60, upgradeCost: null, upgradeHours: null },
  { level: 2, name: 'Fejlett állomás', output: 1.35, metalUpkeep: 22, miningEnergyUpkeep: 95, upgradeCost: { metal: 450, energy: 225 }, upgradeHours: 3 },
  { level: 3, name: 'Ipari komplexum', output: 1.75, metalUpkeep: 40, miningEnergyUpkeep: 150, upgradeCost: { metal: 1200, energy: 600 }, upgradeHours: 6 },
  { level: 4, name: 'Nehézipari központ', output: 2.15, metalUpkeep: 66, miningEnergyUpkeep: 220, upgradeCost: { metal: 2800, energy: 1400 }, upgradeHours: 12 },
  { level: 5, name: 'Mega-komplexum', output: 2.6, metalUpkeep: 105, miningEnergyUpkeep: 310, upgradeCost: { metal: 6000, energy: 3000 }, upgradeHours: 24 },
];
export const MAX_STATION_LEVEL = STATION_TIERS.length;

export function stationTier(level: number): StationTier {
  return STATION_TIERS[Math.min(Math.max(Math.round(level), 1), MAX_STATION_LEVEL) - 1]!;
}

// ---- Logistics by distance ----

export const LOGISTICS = {
  /** Within this many ly of a hub the transport is lossless (about one lane). */
  freeRadiusLy: 70,
  /** Loss of delivered output per ly beyond the free radius. */
  lossPerLy: 0.004,
  /** A station never delivers less than this share. */
  minEfficiency: 0.35,
  hub: {
    metal: 300,
    energy: 120,
    hours: 6,
    metalUpkeep: 30,
    energyUpkeep: 25,
  },
  /** At most this many logistics hubs for an empire owning `systems` systems (home included). */
  maxHubs: (systems: number): number => 1 + Math.floor(Math.max(0, systems - 1) / 4),
} as const;

/** Share of a station's output that reaches the stockpile, from its distance (ly) to the nearest hub. */
export function transportEfficiency(distanceLy: number): number {
  const loss = Math.max(0, distanceLy - LOGISTICS.freeRadiusLy) * LOGISTICS.lossPerLy;
  return Math.max(LOGISTICS.minEfficiency, Math.round((1 - loss) * 1000) / 1000);
}

export type HubKind = 'capital' | 'hub' | 'trade';
export const HUB_KIND_LABEL: Record<HubKind, string> = {
  capital: 'főváros',
  hub: 'elosztóközpont',
  trade: 'kereskedelmi csomópont',
};

export interface LogisticsNode { systemId: string; x: number; y: number; kind: HubKind }

/** Nearest node by straight-line distance in ly (ties: capital first, then lower systemId). */
export function nearestHub(x: number, y: number, nodes: readonly LogisticsNode[]): { node: LogisticsNode; distanceLy: number } | null {
  const rank: Record<HubKind, number> = { capital: 0, hub: 1, trade: 2 };
  let best: { node: LogisticsNode; distanceLy: number } | null = null;
  for (const node of nodes) {
    const d = Math.hypot(node.x - x, node.y - y);
    if (!best || d < best.distanceLy - 1e-9
      || (Math.abs(d - best.distanceLy) <= 1e-9 && (rank[node.kind] < rank[best.node.kind] || (rank[node.kind] === rank[best.node.kind] && node.systemId < best.node.systemId)))) {
      best = { node, distanceLy: d };
    }
  }
  return best;
}

// ---- Scouts (surveys gate expansion: an outpost needs a surveyed site) ----

export const SCOUT = {
  metal: 80,
  energy: 20,
  hours: GAME_HOURS.scout,
  /** Most scouts (built and ordered) an empire owning `systems` systems may have. */
  maxScouts: (systems: number): number => Math.min(3, 1 + Math.floor(Math.max(0, systems - 1) / 5)),
} as const;

// ---- Builder ships ----

export const BUILDER = {
  metal: 150,
  energy: 40,
  hours: GAME_HOURS.builder,
  /** Hourly upkeep of every builder beyond the free first one. */
  metalUpkeep: 4,
  energyUpkeep: 8,
  /** Most builders an empire owning `systems` systems may have (built and ordered). */
  maxBuilders: (systems: number): number => Math.min(6, 2 + Math.floor(Math.max(0, systems - 1) / 3)),
} as const;
